import { DeleteOutline, KeyboardArrowDown } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { CONSTANTS } from '../../../Constants/Constant'
import { getDateInFormat } from '../../../Helpers/Util'
import AssetStateLabel from '../../Labels/AssetStateLabel'
import InOutWindow from '../../ManageContent/ManageAsset/AssetEditor/InOutWindow'
import { MUIGrid } from '../../Shared'
import CommonBadge from '../../Shared/Badges/CommonBadge'
import MUITooltip from '../../Shared/MUI-Tooltip'
import style from '../Molecule.module.scss'

interface HeaderComponentModal {
  isAnyMetaPermission?: boolean
  assetType?: string
  title?: string
  status?: string
  blitzId?: string | number
  contentID?: number | string
  creationDate?: any
  updatedTime?: any
  lastPublishedOn?: any
  onDeleteIconClick?: () => void
  assetOtherDetails?: any
  assetDetails?: any
  isAuditTrail?: boolean
  isBaseAsset?: string | boolean | null
}
const AssetHeaderComponent: React.FC<HeaderComponentModal> = ({
  isAnyMetaPermission = true,
  assetType = CONSTANTS.DEFAULT_TEXT,
  title = CONSTANTS.DEFAULT_TEXT,
  status = CONSTANTS.DEFAULT_TEXT,
  blitzId = CONSTANTS.DEFAULT_TEXT,
  contentID = CONSTANTS.DEFAULT_TEXT,
  creationDate = CONSTANTS.DEFAULT_TEXT,
  // updatedTime = CONSTANTS.DEFAULT_TEXT,
  lastPublishedOn = CONSTANTS.DEFAULT_TEXT,
  onDeleteIconClick,
  assetOtherDetails = {},
  isAuditTrail = false,
  isBaseAsset,
}) => {
  const { pageType } = useParams()
  const [showDetails, setShowDetails] = useState(false)
  const canDelete =
    status !== CONSTANTS.ASSET_STATUS.PUBLISHED && status !== CONSTANTS.ASSET_STATUS.CREATED

  const partnerContentId =
    assetOtherDetails?.contentDetails?.partnerContentId ??
    assetOtherDetails?.partnerContentId ??
    assetOtherDetails?.contentDetails?.legacyHouseId ??
    assetOtherDetails?.legacyHouseId
  const normalizedPartnerContentId = `${partnerContentId ?? ''}`.trim()
  const hasPartnerContentId =
    normalizedPartnerContentId !== '' && normalizedPartnerContentId.toLowerCase() !== 'null'

  const copyTextToClipboard = async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
    } catch (err) {
      // Fallback below for environments without clipboard API support
    }

    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  const handlePartnerContentIdCopy = async (event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation()
    if (!hasPartnerContentId) return
    await copyTextToClipboard(normalizedPartnerContentId)
  }

  const getAssetId = () => {
    if (!isAuditTrail) {
      return (
        <span className='d-flex align-items-center gap-8'>
          <span>{blitzId}</span>
          <MUITooltip
            title={hasPartnerContentId ? `Partner Content ID: ${normalizedPartnerContentId}` : ''}
            disableInteractive={true}
          >
            <span
              data-testid='partner_content_info_icon'
              onClick={handlePartnerContentIdCopy}
              className='d-flex align-items-center'
              style={{
                color: hasPartnerContentId ? '#1A237E' : '#A0A5B1',
                cursor: hasPartnerContentId ? 'pointer' : 'not-allowed',
              }}
              aria-disabled={!hasPartnerContentId}
              aria-label='Partner Content ID'
            >
              <InfoOutlinedIcon fontSize='small' />
            </span>
          </MUITooltip>
          {showDetails ? (
            <KeyboardArrowUpIcon className='col33' />
          ) : (
            <KeyboardArrowDown className='col33' />
          )}
        </span>
      )
    } else {
      return <>{contentID} </>
    }
  }

  const { contractStartDate, contractEndDate } = assetOtherDetails
  const showBaseAsset = isBaseAsset === 'true' || isBaseAsset === true
  const showVariantAsset = isBaseAsset === 'false' || isBaseAsset === false

  return (
    <div style={{ position: 'relative' }} className={isAuditTrail ? 'mb-1 mt-2' : ''}>
      <div
        className={`asset-header-badge${
          contractStartDate || contractEndDate ? ' in-out-window' : ''
        }`}
      >
        {assetOtherDetails?.avsInactiveMigrated && (
          <CommonBadge text={CONSTANTS.AVS_INACTIVE_MIGRATED} color='primary' />
        )}
        {showBaseAsset && <CommonBadge text={CONSTANTS.BASE_ASSET} color='success' />}
        {showVariantAsset && <CommonBadge text={CONSTANTS.VARIANT_ASSET} color='black' />}
        <InOutWindow assetOtherDetails={assetOtherDetails} />
      </div>
      <div
        className={`${isAuditTrail ? '' : 'cursor-pointer '}mt-1`}
        data-testid='asset_header_accordion'
        onClick={() => {
          if (!isAuditTrail) setShowDetails(!showDetails)
        }}
      >
        <MUIGrid
          container
          className={`d-flex justify-content-between align-items-start ${style.asset_header_static}`}
        >
          <MUIGrid className='d-flex align-items-center gap-12'>
            <span style={{ fontWeight: '400' }} className='col34'>
              Asset Type :
            </span>
            <span className={`col36 capitalize ${style.f_12_w_600}`}>{assetType}</span>
            <span className='col36'>
              <AssetStateLabel state={status} />
            </span>
          </MUIGrid>
          <MUIGrid
            className='d-flex align-items-start gap-6'
            style={{ maxWidth: 'calc(100% - 560px)' }}
          >
            <span className='col34' style={{ fontWeight: '400' }}>
              Title :
            </span>
            <span
              className={`col36 ${style.header_value} textEllipsesLine-3 word-break-all mt-neg2`}
            >
              {title || CONSTANTS.DEFAULT_TEXT}
            </span>
          </MUIGrid>
          <MUIGrid className='d-flex align-items-center gap-12'>
            <span className='col34'>{isAuditTrail ? 'Content Id' : 'Blitz Id'} :</span>
            <span className={`col36 ${style.w_600}`}>
              <span className='d-flex align-items-center'>{getAssetId()}</span>
            </span>
          </MUIGrid>
        </MUIGrid>
      </div>
      {showDetails || isAuditTrail ? (
        <MUIGrid
          container
          id='create-asset-header-details'
          className={`d-flex justify-content-between align-items-center ${style.asset_header_dynamic_bottom}`}
        >
          <MUIGrid className='d-flex align-items-center gap-12'>
            <span style={{ fontWeight: '400' }} className='col34'>
              Creation Date :
            </span>
            <span className={`col36 ${style.header_value}`}>{creationDate}</span>
          </MUIGrid>
          {/* <MUIGrid className='d-flex align-items-center gap-12'>
            <span style={{ fontWeight: '400' }} className='col34'>
              Last Updated On :
            </span>
            <span className={`col36 ${style.header_value}`}>{updatedTime}</span>
          </MUIGrid> */}
          <MUIGrid className='d-flex align-items-center gap-12'>
            <span style={{ fontWeight: '400' }} className='col34'>
              Last Published On :
            </span>
            <span className={`col36 ${style.header_value}`}>
              {assetOtherDetails?.lastPublished
                ? getDateInFormat(assetOtherDetails?.lastPublished, CONSTANTS.DATE_FORMAT_TIME)
                : lastPublishedOn}
            </span>
          </MUIGrid>
          <div className='d-flex align-items-center gap-18'>
            {!isAuditTrail && assetType !== 'VIDEO_SHORTS' && (
              <MUIGrid className='d-flex align-items-center gap-12'>
                <span style={{ fontWeight: '400' }} className='col34'>
                  Content Id :
                </span>
                <span className={`col36 ${style.header_value}`}>{contentID}</span>
              </MUIGrid>
            )}
            {pageType !== CONSTANTS.PAGE_TYPE.VIEW_ONLY && onDeleteIconClick && (
              <div
                onClick={() => {
                  if (canDelete && isAnyMetaPermission) onDeleteIconClick()
                }}
                className={`col37 d-flex align-items-center ${
                  canDelete && isAnyMetaPermission ? 'cursor-pointer' : 'disabled'
                }`}
                data-testid='deleteIcon__test'
              >
                <MUITooltip title='Delete Asset Version' disableInteractive={true}>
                  <DeleteOutline className='col37' />
                </MUITooltip>
              </div>
            )}
          </div>
        </MUIGrid>
      ) : (
        <></>
      )}
    </div>
  )
}

export default AssetHeaderComponent
